import { useState } from "react";
import CardContent from "@mui/joy/CardContent";
import Stack from "@mui/joy/Stack";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import Card from "@mui/joy/Card";

const colorPalette = ['#e2af61', '#d298c4', '#6f828e', '#f2c003', '#e35b27', '#ab2d2c'];

const INK = "#15171C";

// Donut geometry
const SIZE = 180;
const CENTER = SIZE / 2;
const RADIUS = 64;
const THICKNESS = 26;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const GAP = 6; // visual gap between slices, in path-length units

const PieChartCard = (data) => {
    const name = data.name
    const time = data.time
    const chartData = data.chartData

    const [hovered, setHovered] = useState(null);

    function channel_name_cleaner(name) {
        name = name.replaceAll('Album of the Week', 'AOTW');
        name = name.replaceAll('Season 2', 'S2');
        return name;
    }

    const slices = chartData.slice(0, 5).map((item, index) => ({
        id: item['id'],
        value: item['message_count'],
        label: channel_name_cleaner(item['label']),
        color: colorPalette[index],
    }));

    const total = slices.reduce((sum, slice) => sum + slice.value, 0);

    // Pre-compute each arc's drawn length and starting offset along the circle.
    let cursor = 0;
    const arcs = slices.map((slice) => {
        const fraction = total > 0 ? slice.value / total : 0;
        const segment = fraction * CIRCUMFERENCE;
        const drawn = Math.max(0, segment - GAP);
        const offset = cursor;
        cursor += segment;
        return { ...slice, drawn, offset, percent: Math.round(fraction * 100) };
    });

    return (
        <Card
            variant="plain"
            sx={{
                backgroundColor: '#FFFFFF',
                width: '100%',
                borderRadius: '28px',
                p: 3,
            }}
        >
            <CardContent>
                <Stack direction="column" spacing={0}>
                    <Typography level='h3' sx={{ textAlign: 'left', color: INK, fontWeight: 700 }}>
                        {name}
                    </Typography>
                    <Typography level='body-sm' sx={{ textAlign: 'left', color: INK, fontWeight: 600 }}>
                        Since {time} ago
                    </Typography>
                </Stack>
            </CardContent>

            <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ alignItems: 'center', minWidth: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', flexShrink: 0, maxWidth: '100%' }}>
                        <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} role="img" aria-label={name} style={{ maxWidth: '100%', height: 'auto' }}>
                            {/* rotate so the first slice starts at 12 o'clock, going clockwise */}
                            <g transform={`rotate(-90 ${CENTER} ${CENTER})`}>
                                {arcs.map((arc, index) => (
                                    <circle
                                        key={arc.id}
                                        cx={CENTER}
                                        cy={CENTER}
                                        r={RADIUS}
                                        fill="none"
                                        stroke={arc.color}
                                        strokeWidth={hovered === index ? THICKNESS + 6 : THICKNESS}
                                        strokeLinecap="round"
                                        strokeDasharray={`${arc.drawn} ${CIRCUMFERENCE - arc.drawn}`}
                                        strokeDashoffset={-arc.offset}
                                        style={{
                                            opacity: hovered === null || hovered === index ? 1 : 0.4,
                                            transition: 'opacity 150ms ease, stroke-width 150ms ease',
                                            cursor: 'pointer',
                                        }}
                                        onMouseEnter={() => setHovered(index)}
                                        onMouseLeave={() => setHovered(null)}
                                    >
                                        <title>{`${arc.label}: ${arc.value} (${arc.percent}%)`}</title>
                                    </circle>
                                ))}
                            </g>
                        </svg>
                    </Box>

                    <Stack direction="column" spacing={1} sx={{ minWidth: 0, width: '100%' }}>
                        {arcs.map((arc, index) => (
                            <Stack
                                key={arc.id}
                                direction="row"
                                spacing={1.5}
                                sx={{
                                    alignItems: 'center',
                                    minWidth: 0,
                                    opacity: hovered === null || hovered === index ? 1 : 0.4,
                                    transition: 'opacity 150ms ease',
                                    cursor: 'default',
                                }}
                                onMouseEnter={() => setHovered(index)}
                                onMouseLeave={() => setHovered(null)}
                            >
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        backgroundColor: arc.color,
                                        flexShrink: 0,
                                    }}
                                />
                                <Typography level='body-sm' sx={{ color: INK, fontWeight: 700, flexShrink: 0 }}>
                                    {arc.percent}%
                                </Typography>
                                <Typography level='body-sm' sx={{ color: INK, minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                                    {arc.label}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}

export default PieChartCard;
